import { PlatformApiMethods } from '../../../../types/platformApi';
import {
  UnifiedPage,
  UnifiedWidget,
} from '../../../../types/unifiedComponents';
import { IntentInstallStore } from '../IntentInstallStore';
import { ProgressBarManager } from '../ProgressBarManager';
import { findAsyncSequential } from '../helpers';
import { loadPanel } from './loadPanel';
import type { IntentModalsProps, WidgetsConfiguration } from './ui/types';
import { IntentInstallPanelsNames } from './ui/types';

const token = '';

type PanelPromiseCallback<T> = (resolve: (data: T) => void) => void;

export class PanelsManager {
  constructor(
    private platformApiMethods: PlatformApiMethods,
    private intentInstallStore: IntentInstallStore,
    private progressBarManager: ProgressBarManager,
  ) {}

  private async openPlatformPanel<T extends IntentInstallPanelsNames>(
    panelName: T,
    props: IntentModalsProps[T],
  ) {
    this.platformApiMethods.editor.openPanel(token, panelName, props, {
      name: panelName,
      panelName,
      panelLoader: () => loadPanel(panelName),
      componentRef: {},
      openTime: Date.now(),
      closeWithUserIntent: true,
      frameType: 'platform',
    });
  }

  private createPanelPromise<T>(cb: PanelPromiseCallback<T>) {
    return new Promise<T>((resolve) => {
      const resumeProgressBar = this.progressBarManager.pauseProgressBar();

      const wrappedResolve = (value: T) => {
        resolve(value);
        resumeProgressBar();
      };
      cb(wrappedResolve);
    });
  }

  public openPreIntentInstallModal() {
    const intentApp = this.intentInstallStore.getIntentApp();

    if (!intentApp) {
      return;
    }

    const otherAppsToInstall =
      this.intentInstallStore.getAppNamesToInstallExceptIntentApp();

    if (!otherAppsToInstall.length) {
      return;
    }

    const intentAppName = this.intentInstallStore.getIntentAppName()!;

    return this.createPanelPromise<void>((resolve) => {
      this.openPlatformPanel(IntentInstallPanelsNames.PreIntentInstallModal, {
        intentAppName,
        otherAppsToInstall,
        closePanel: () => {
          this.platformApiMethods.editor.closePanel(
            IntentInstallPanelsNames.PreIntentInstallModal,
          );
          resolve();
        },
      });
    });
  }

  public openWidgetAppsIntentInstallModal(
    widgets: UnifiedWidget[],
    pages: UnifiedPage[],
    appName: string,
  ) {
    return this.createPanelPromise<WidgetsConfiguration>((resolve) => {
      this.openPlatformPanel(
        IntentInstallPanelsNames.WidgetAppsIntentInstallModal,
        {
          appName,
          widgets,
          pages,
          onSubmit: (widgetsConfig: WidgetsConfiguration) => {
            this.platformApiMethods.editor.closePanel(
              IntentInstallPanelsNames.WidgetAppsIntentInstallModal,
            );
            resolve(widgetsConfig);
          },
          onCancel: () => {
            this.platformApiMethods.editor.closePanel(
              IntentInstallPanelsNames.WidgetAppsIntentInstallModal,
            );
            resolve({});
          },
        },
      );
    });
  }

  public openPlatformFailedAlert() {
    const appsWithErrors = this.intentInstallStore.getAppsWithErrorsNames();
    if (!appsWithErrors.length) {
      return;
    }

    return this.platformApiMethods.editor.openPanel(
      token,
      'savePublish.panels.common.failPanel',
      {
        titleKey: 'Notification_Box_App_Installation_Error_Title',
        description: 'Notification_Box_App_Installation_Error_Text',
        steps: [
          'Notification_Box_App_Installation_Error_Step1',
          'Notification_Box_App_Installation_Error_Step2',
        ],
        symbolName: 'serverError_NewWorkspace',
        helpMessage: '',
        helpLinkMessage: '',
        helpLink: '',
        translateArgs: {
          description: {
            appName: appsWithErrors.join(', '),
          },
        },
      },
      false,
    );
  }

  private async checkIfShouldOpenBusinessManager(appDefId: string) {
    const isAppSupportedByBusinessManager =
      await this.platformApiMethods.editor.isAppSupportedByBusinessManager?.(
        {},
        '',
        {
          appDefinitionId: appDefId,
        },
      );

    return (
      isAppSupportedByBusinessManager &&
      this.intentInstallStore.isAppPublished(appDefId)
    );
  }

  public async openBusinessPanel() {
    const isSilent = this.intentInstallStore.isSilent();

    const intentApp = this.intentInstallStore.getIntentApp();
    const appDefIds = isSilent
      ? this.intentInstallStore.getAppsToInstall()
      : intentApp
      ? [intentApp]
      : [];

    const selectedAppId = await findAsyncSequential(appDefIds, (appDefId) =>
      this.checkIfShouldOpenBusinessManager(appDefId),
    );

    if (!selectedAppId) {
      return;
    }

    this.platformApiMethods.editor.openPanel(
      token,
      'appManager.appManagerPanel',
      { selectedAppId },
      false,
    );
  }
}
